import Dialog from "@components/Dialog"
import PlanFooterText from "@components/PlanFooterText"
import SubscriptionForm from "@components/SubscriptionForm"
import getPriceForCurrency from "@helpers/getPriceForCurrency"
import { getOnboardingContext, isHomeFlow } from "@helpers/localStorage"
import localizeCurrency from "@helpers/localizeCurrency"
import { useTranslation } from "react-i18next"
import { Plan, StripePrice } from "src/types"

interface Props {
  currency: string
  isSubmitting: boolean
  open: boolean
  onClose: () => void
  plan: Plan
  setIsSubmitting: (isSubmitting: boolean) => void
  stripeIntroPrice: StripePrice | undefined
  stripePrice: StripePrice | undefined
}

export default function LimitedTimeOfferDialog({
  currency,
  isSubmitting,
  onClose,
  open,
  plan,
  setIsSubmitting,
  stripeIntroPrice,
  stripePrice,
}: Props) {
  const { t } = useTranslation()
  const context = getOnboardingContext()
  const plansCopyKey =
    context === "cars"
      ? "pages.checkout.plansCars"
      : context === "pets"
        ? "pages.checkout.plansPets"
        : isHomeFlow()
          ? "pages.checkout.plansHome"
          : "pages.checkout.plans"

  const priceData = getPriceForCurrency(stripePrice, currency)
  const introPriceData = getPriceForCurrency(stripeIntroPrice, currency)

  if (!priceData || !introPriceData || !stripeIntroPrice) return null

  const localizePrice = (price: number) =>
    localizeCurrency({ amountInCents: price, currency: priceData.currency })

  const getPlanLabel = () => {
    switch (plan) {
      case "weekly":
        return t(`${plansCopyKey}.oneWeek`)
      case "monthly":
        return t(`${plansCopyKey}.oneMonth`)
      case "quarterly":
        return t(`${plansCopyKey}.threeMonths`)
      case "yearly":
        return t(`${plansCopyKey}.twelveMonths`)
      case "one-time":
        return t(`${plansCopyKey}.oneTimeLabel`)
    }
  }

  const planLabel = getPlanLabel()

  return (
    <Dialog onClose={onClose} open={open}>
      <div className="space-y-4 text-center">
        <h2 className="text-2xl font-semibold">
          {t(`${plansCopyKey}.dialog.limitedTimeOffer`)}
        </h2>

        <p className="font-semibold tracking-wide uppercase">{planLabel}</p>

        <div className="space-y-1">
          <p className="text-text-secondary line-through">
            {localizePrice(priceData.price)}
          </p>
          <p className="text-2xl font-semibold">
            {localizePrice(introPriceData.price)}
          </p>
        </div>

        <SubscriptionForm
          buttonClassName="theme-button-primary-black uppercase"
          buttonTitle={t(`${plansCopyKey}.continue`)}
          currency={priceData.currency}
          fullPrice={stripePrice}
          isSubmitting={isSubmitting}
          plan={plan}
          priceId={stripeIntroPrice.id}
          setIsSubmitting={setIsSubmitting}
        />

        <PlanFooterText plan={plan} />
      </div>
    </Dialog>
  )
}
